import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AdminLayout from "../../../components/layout/AdminLayout";
import { getOrders, updateItemQuantity, removeItemFromOrder, updateOrderStatus } from "../../../redux/actions/orderActions";
import { FiSearch, FiEye, FiEdit2, FiTrash2 } from "react-icons/fi";
import { Modal, Button, Form } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MdLocationOn } from "react-icons/md";
import { toast } from "sonner";
import { logoApplication } from "../../../config/dataApi";

// Icône personnalisée pour Leaflet
const customIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [32, 32],
});

const OrdersList = () => {
  const dispatch = useDispatch();
  const { orders, loading, error } = useSelector((state) => state.orders);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  useEffect(() => {
    dispatch(getOrders());
  }, [dispatch]);

  // Calcul du prix total de la commande
  const calculateOrderTotal = (order) => {
    return order.items.reduce((total, item) => {
      const price = item.product?.price || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const filteredOrders = orders.filter(
    (order) =>
      order.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Gestion du changement de statut
  const handleUpdateStatus = async () => {
  try {
    // Vérifie si les paramètres nécessaires existent
    if (!selectedOrder?._id) {
      toast.error("⚠️ Impossible de changer le statut : commande introuvable.");
      return;
    }

    if (!newStatus) {
      toast.warning("Veuillez sélectionner un nouveau statut avant de valider.");
      return;
    }

    // Appel API via Redux
    dispatch(updateOrderStatus(selectedOrder._id, newStatus));

    // Ferme les modales une fois terminé
    setShowStatusModal(false);
    setSelectedOrder(null);

  } catch (err) {
    console.error("Erreur lors du changement de statut :", err);
    toast.error("Une erreur est survenue lors du changement de statut.");
  }
};


  return (
    <AdminLayout>
      {/* Titre */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h4 fw-bold">Gestion des Commandes</h1>
      </div>

      {/* Barre de recherche */}
      <div className="mb-4">
        <div className="input-group">
          <span className="input-group-text">
            <FiSearch />
          </span>
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            className="form-control"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center my-5 py-5">
          <div className="spinner-border text-danger" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      ) : (
        <div className="card shadow-sm">
          {filteredOrders.length === 0 ? (
            <div className="card-body text-center text-muted">
              Aucune commande trouvée.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Client</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Méthode Paiement</th>
                    <th>Statut</th>
                    <th>Montant Total (F)</th>
                    <th>Date</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order._id}>
                      <td>{order.firstName} {order.lastName}</td>
                      <td>{order.email || "—"}</td>
                      <td>{order.phone || "—"}</td>
                      <td>
                        <span className="badge bg-info text-dark">
                          {order.paymentMethod || "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            order.status === "delivered"
                              ? "bg-success"
                              : order.status === "cancelled"
                              ? "bg-danger"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td>
                        <strong>{calculateOrderTotal(order).toLocaleString()}</strong>
                      </td>
                      <td>
                        {new Date(order.createdAt).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => setSelectedOrder(order)}
                        >
                          <FiEye size={16} /> Voir
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success me-2"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowStatusModal(true);
                          }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        {/* <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => toast("Suppression non encore implémentée")}
                        >
                          <FiTrash2 size={16} />
                        </button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal détail commande */}
      <Modal show={!!selectedOrder && !showStatusModal} onHide={() => setSelectedOrder(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Détails de la commande</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <>
              <h5 className="fw-bold mb-3">
                {selectedOrder.firstName} {selectedOrder.lastName}
              </h5>
              <p>
                <strong>Email :</strong> {selectedOrder.email || "—"}<br />
                <strong>Téléphone :</strong> {selectedOrder.phone || "—"}<br />
                <strong>Adresse :</strong> {selectedOrder.address || "—"}<br />
                <strong>Méthode :</strong> {selectedOrder.paymentMethod || "—"}<br />
                <strong>Statut :</strong>{" "}
                <span className="badge bg-secondary">{selectedOrder.status}</span><br />
                <strong>Total :</strong> {calculateOrderTotal(selectedOrder).toLocaleString()} F
              </p>

              <hr />
              <h6 className="fw-bold">Produits commandés :</h6>
              <ul className="list-group mb-3">
                {selectedOrder.items.map((item, i) => (
                  <li
                    key={i}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <img
                      src={item.product?.images?.[0] || logoApplication}
                      alt=""
                      style={{ height: "50px", width: "50px", borderRadius: "8px" }}
                    />
                    <span>{item.product?.name || "Produit inconnu"}</span>
                    <span className="badge bg-primary">x{item.quantity}</span>
                  </li>
                ))}
              </ul>

              {selectedOrder.lat && selectedOrder.lng ? (
                <div style={{ height: "300px", borderRadius: "8px" }}>
                  <MapContainer
                    center={[selectedOrder.lat, selectedOrder.lng]}
                    zoom={14}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[selectedOrder.lat, selectedOrder.lng]} icon={customIcon}>
                      <Popup>
                        {selectedOrder.firstName} {selectedOrder.lastName}
                        <br />
                        {selectedOrder.address}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              ) : (
                <div className="text-muted text-center">
                  <MdLocationOn size={20} className="me-1" />
                  Aucune position GPS disponible.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedOrder(null)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal changement de statut */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Modifier le statut</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
            <option value="">Choisir un statut</option>
            {/* <option value="pending">En attente</option> */}
            {/* <option value="processing">En traitement</option> */}
            {/* <option value="shipped">Expédiée</option> */}
            <option value="delivered">Livrée</option>
            <option value="cancelled">Annulée</option>
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateStatus} disabled={!newStatus}>
            Valider
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminLayout>
  );
};

export default OrdersList;
